/**
 * @file useMessageSender.ts
 * @description Отправка ответа клиенту и черновики (chat-window-design.md
 * §5 «Отправка», «Не отправлено», §6 «Черновики»).
 *
 * Пузырь появляется в ленте сразу («Отправляется…»); сбой — пузырь с красной
 * рамкой «Не отправлено. Текст сохранён» [Повторить] [Удалить]. Отказ из-за
 * рекламы без согласия — текст возвращается в поле, а не висит пузырём:
 * повтор с той же пометкой всё равно не пройдёт. Черновик живёт в
 * `sessionStorage` вкладки: `commora:chat:draft:<shopId>:<conversationId>`.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { useNotify } from "../ui/NotifyProvider";
import { ApiError } from "../../lib/api-client";
import { adminChat, type ChatMessageDto } from "../../lib/api-chat";
import { chatErrorText } from "./chat-errors";
import { chatStorageKeys } from "./chat-window-state";

export interface PendingMessage {
  tempId: string;
  body: string;
  isMarketing: boolean;
  createdAt: string;
  status: "sending" | "failed";
}

/** Черновик поля ввода по диалогу. */
export function useDraft(shopId: string, conversationId: string | null) {
  const key = conversationId ? chatStorageKeys.draft(shopId, conversationId) : null;
  const [value, setValue] = useState(() => (key ? (window.sessionStorage.getItem(key) ?? "") : ""));

  useEffect(() => {
    setValue(key ? (window.sessionStorage.getItem(key) ?? "") : "");
  }, [key]);

  const update = useCallback(
    (next: string) => {
      setValue(next);
      if (!key) return;
      if (next) window.sessionStorage.setItem(key, next);
      else window.sessionStorage.removeItem(key);
    },
    [key],
  );

  return [value, update] as const;
}

interface SenderOptions {
  onSent: (message: ChatMessageDto) => void;
  /** Диалог ушёл в архив или сменился — перечитать карточку. */
  onStale: () => void;
  /** Отказ по рекламе: вернуть текст в поле. */
  onRestoreDraft: (body: string) => void;
}

let tempSeq = 0;

export function useMessageSender(conversationId: string | null, options: SenderOptions) {
  const notify = useNotify();
  const [pending, setPending] = useState<PendingMessage[]>([]);
  // Обработчики — через ref: `send` / `retry` / `remove` стабильны, лента в
  // `memo` не перерисовывается из-за новых функций на каждую букву.
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  useEffect(() => setPending([]), [conversationId]);

  const deliver = useCallback(
    async (item: PendingMessage) => {
      if (!conversationId) return;
      try {
        const message = await adminChat.send(conversationId, item.body, item.isMarketing);
        setPending((list) => list.filter((p) => p.tempId !== item.tempId));
        optionsRef.current.onSent(message);
      } catch (error) {
        const code = error instanceof ApiError ? error.code : "";
        if (code === "marketing_chat_consent_required") {
          setPending((list) => list.filter((p) => p.tempId !== item.tempId));
          optionsRef.current.onRestoreDraft(item.body);
          notify.error(chatErrorText(error, ""));
          return;
        }
        if (code === "chat_conversation_archived" || code === "chat_impersonation_read_only") optionsRef.current.onStale();
        setPending((list) => list.map((p) => (p.tempId === item.tempId ? { ...p, status: "failed" } : p)));
        notify.error(chatErrorText(error, "Не удалось отправить сообщение. Текст сохранён — повторите отправку"));
      }
    },
    [conversationId, notify],
  );

  const send = useCallback(
    (body: string, isMarketing: boolean) => {
      tempSeq += 1;
      const item: PendingMessage = {
        tempId: `tmp-${tempSeq}`,
        body,
        isMarketing,
        createdAt: new Date().toISOString(),
        status: "sending",
      };
      setPending((list) => [...list, item]);
      void deliver(item);
    },
    [deliver],
  );

  const retry = useCallback(
    (tempId: string) => {
      const item = pendingRef.current.find((p) => p.tempId === tempId);
      if (!item) return;
      const again = { ...item, status: "sending" as const };
      setPending((list) => list.map((p) => (p.tempId === tempId ? again : p)));
      void deliver(again);
    },
    [deliver],
  );

  const remove = useCallback((tempId: string) => setPending((list) => list.filter((p) => p.tempId !== tempId)), []);

  return { pending, send, retry, remove };
}
